import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { cn } from '@/lib/utils'

import { Seo, Layout } from '@/components/generals'

interface Plan {
    listId: string
    itemId: string
    name: string
    price: string
    description: string
    featured: boolean
    features: string[]
    cta: { label: string; href: string }
}

const plans: Plan[] = [
    {
        listId: 'w-node-bb91ceaa-06d0-2ec1-a620-bfcd18bff0df-98f5df8d',
        itemId: 'w-node-_0006f261-face-d8e0-43a5-88aa60d5be8f-98f5df8d',
        name: 'Basic',
        price: '$9',
        description: 'For freelancers tracking their own time.',
        featured: false,
        features: ['Unlimited time tracking', 'Up to 5 active projects', 'Weekly summary reports', 'iOS & Android apps', 'Email support'],
        cta: { label: 'Get started', href: '/#sign-up' }
    },
    {
        listId: 'w-node-_986159ab-d85b-4506-4c82-583c6f9332ef-98f5df8d',
        itemId: 'w-node-_986159ab-d85b-4506-4c82-583c6f9332f2-98f5df8d',
        name: 'Pro',
        price: '$19',
        description: 'For small teams that need more power.',
        featured: true,
        features: ['Everything in Basic', 'Unlimited projects', 'Automatic time tracking', 'Custom reports & invoicing', 'Calendar & tool integrations', 'Priority support'],
        cta: { label: 'Get started', href: '/#sign-up' }
    },
    {
        listId: 'w-node-_2cb91dee-387e-ee81-fab7-0ec4c2c9e9cb-98f5df8d',
        itemId: 'w-node-_2cb91dee-387e-ee81-fab7-0ec4c2c9e9ce-98f5df8d',
        name: 'Business',
        price: '$39',
        description: 'For organizations running at scale.',
        featured: false,
        features: ['Everything in Pro', 'SSO & advanced security', 'Team analytics dashboard', 'Dedicated account manager', 'Onboarding & SLA'],
        cta: { label: 'Contact sales', href: '/contact' }
    }
]

const Pricing: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Pricing' description='Simple, easy pricing for Black+. Start your free trial and choose the plan that fits your team.' />

            {/* Pricing hero */}
            <div className='section'>
                <div className='container no-paddings'>
                    <div className='pricing-hero'>
                        <div className='center-title'>
                            <div className='title-holder'>
                                <div className='animate-on-load-01'>
                                    <h1 className='white-title'>Simple, easy pricing</h1>
                                </div>
                                <div className='animate-on-load-02'>
                                    <div className='experience-paragraph-holder'>
                                        <p>Pick a plan that fits how you work. Start free, upgrade as your team grows, and only pay for what you use.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='animate-on-load-03'>
                        <div className='pricing-category-holder'>
                            <div className='pricing-category-container'>
                                <div className='pricing-category-wrapper'>
                                    <div className='pricing-category'>
                                        <div className='w-inline-block'>
                                            <div className='pricing-button active'>
                                                <div>Monthly</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='animate-on-load-04'>
                        <div className='pricing-table-holder'>
                            <div className='pricing-table-container'>
                                {plans.map((plan) => (
                                    <div key={plan.itemId} id={plan.listId} className='w-dyn-list'>
                                        <div role='list' className='w-dyn-items'>
                                            <div role='listitem' className='w-dyn-item'>
                                                <div id={plan.itemId} className={cn('pricing-item', plan.featured && 'middle')}>
                                                    <div className='pricing-item-container'>
                                                        <div className='product-link w-inline-block'>
                                                            <div className='pricing-title'>{plan.name}</div>
                                                            <div className='price-holder'>
                                                                <div className='price'>{plan.price}</div>
                                                                <div className='pricing-indicator'>/monthly</div>
                                                            </div>
                                                            <p>{plan.description}</p>
                                                            <div className='pricing-divider' />
                                                        </div>
                                                        <div className='pricing-list-holder'>
                                                            <div className='black-list w-richtext'>
                                                                <ul role='list' style={{ listStyleType: 'none', paddingLeft: 0, marginTop: 0, marginBottom: 0 }}>
                                                                    {plan.features.map((feature) => (
                                                                        <li key={feature}>{feature}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <div className='pricing-button-holder'>
                                                            <Link href={plan.cta.href} className='button add-to-cart'>{plan.cta.label}</Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className='logo-grid-holder'>
                        <div className='w-layout-grid logo-grid'>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dcd8-98f5df8d' className='logo-small-container'><img src='/images/Logo01.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dcda-98f5df8d' className='logo-small-container'><img src='/images/Logo02.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dcdc-98f5df8d' className='logo-small-container'><img src='/images/Logo03.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dcde-98f5df8d' className='logo-small-container'><img src='/images/Logo04.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dce0-98f5df8d' className='logo-small-container'><img src='/images/Logo05.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dce2-98f5df8d' className='logo-small-container'><img src='/images/Logo06.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dce4-98f5df8d' className='logo-small-container'><img src='/images/Logo07.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                            <div id='w-node-f078fdf9-afcd-dd23-e056-d7d58c77dce6-98f5df8d' className='logo-small-container'><img src='/images/Logo08.svg' loading='lazy' alt='Customer logo' className='logo-small' /></div>
                        </div>
                        <div className='fade-in-move-on-scroll'>
                            <p>Trusted by Top-tier product campanies</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ */}
            <div className='section'>
                <div className='container'>
                    <div className='center-title'>
                        <div className='title-holder'>
                            <div className='fade-in-move-on-scroll'>
                                <h2 className='title'>Frequently asked questions</h2>
                            </div>
                            <div className='fade-in-move-on-scroll'>
                                <div className='experience-paragraph-holder'>
                                    <p>Everything you need to know about the product and billing.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='faq-holder'>
                        <div className='faq-container'>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>Is there a free trial available?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>Yes, every plan starts with a 30-day free trial, no credit card required. If you want, we&rsquo;ll set up a free 30-minute onboarding call to get your team up and running.</div>
                                </div>
                            </div>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>Can I change my plan later?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>Absolutely. You can upgrade or downgrade at any time from your account settings, and we prorate the difference automatically.</div>
                                </div>
                            </div>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>What is your cancellation policy?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>You can cancel whenever you like. Your plan stays active until the end of the current billing period, with no cancellation fees.</div>
                                </div>
                            </div>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>Can other info be added to an invoice?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>Yes. You can add your company name, address, VAT number and a custom note to every invoice from the billing section.</div>
                                </div>
                            </div>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>How does billing work?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>Plans are billed monthly or yearly per workspace. You only pay for the seats you use, and yearly billing saves you two months.</div>
                                </div>
                            </div>
                            <div className='faq-block'>
                                <div className='faq-heading'>
                                    <div className='faq-question-text'>How do I change my account email?</div>
                                    <div className='faq-icon-holder'><img src='/images/Plus-Icon.svg' loading='lazy' alt='' className='faq-icon' /></div>
                                </div>
                                <div className='faq-answer-holder'>
                                    <div className='faq-answer'>Head to Account &rarr; Profile, update your email address, and confirm the change from the verification link we send you.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='open-roles-cta-holder'>
                        <div className='open-roles-cta-container'>
                            <div className='open-roles-cta'>
                                <div className='content-holder'>
                                    <h2 className='h3-title'>Still have questions?</h2>
                                    <div className='paragraph-open-roles'>
                                        <p>Can&rsquo;t find the answer you&rsquo;re looking for? Please chat to our friendly team.</p>
                                    </div>
                                </div>
                                <Link href='/contact' className='button w-button'>Get in Touch</Link><img src='/images/Open-Roles-Illustaion.svg' loading='eager' alt='' className='open-roles-illustation' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default Pricing
